import React from "react";

import { Button } from "@common/components/Button";
import { Ellipsis } from "@common/components/Ellipsis";
import { Flex } from "@common/components/Flex";
import { FormControl } from "@common/components/form-control";
import { Modal, ModalHeader, ModalTitle, ModalBody, ModalFooter } from "@common/components/modal";
import Dropdown from "@common/containers/fields/Dropdown";
import { trDts } from "@modules/datasheets/utils/i18n";
import FormatManagement from "@common/components/FormatManagement";
import { MessageBox } from "@common/components/MessageBox";
import { Loading } from "@common/components/loading";

import {
  DatasheetFormatManagementMode,
  DatasheetFormatManagementProps,
} from "./datasheet-format-management.types";
import { DatasheetFormatType } from "@common/dto/datasheet/type";
import { getModuleFolders } from "@common/actions/folders";
import { FolderType } from "@common/dto/folder/type";
import { getFolderFields } from "@common/actions/formats";
import { FieldType } from "@common/dto/field/type";
import { FieldSelector } from "@common/components/FieldSelector";
import { FieldSelectorGetFieldParams, FieldSelectorOnChangeSingle } from "@common/components/FieldSelector/field-selector.types";
import { useController } from "./datasheet-format-management.controller";
import { RenameFormatModal } from "./components/rename-format-modal";

import "./datasheet-format-management.styles.scss";
import { FieldPrimaryListDto } from "@common/dto/field/list-primary-dto";

export const DatasheetFormatManagement: React.FC<DatasheetFormatManagementProps> = (props) => {
  const { onClose, getId, isAllowedToSubmit, isAllowedToSubmitFormatManagementSubmit, mode, isExporting = false } = props;

  const {
    defaultFormatTypes,
    selectedFormatType,
    formatName,
    folders,
    formats,
    selectedFolder,
    selectedFormat,
    selectedTableFieldName,
    folderPickerOpened,
    getAndSetFolders,
    handleFolderSelect,
    getAndSetFormats,
    handleSubmit,
    openFolderPicker,
    closeFolderPicker,
    handleFormatNameChange,
    handleTableFieldChange,
    setSelectedFormatType,
    resetFormats,
    modeConfig,
    closeMessage,
    messageProps,
    renameModalOpened,
    renameFormatName,
    closeRenameModal,
    handleRenameConfirm,
    checkFormatNameExists,
    successToastMessage,
    clearSuccessToastMessage,
  } = useController(props);

  // Helpers to load folders and fields for the table-field picker (TDS)
  const getTableFieldFolders = React.useCallback(async () => {
    // For table datasheets in the new React builder we only support
    // Individual-level table fields, so only expose Individual Data Folders.
    const individualRoots = await getModuleFolders({
      folderTypeId: FolderType.individual,
      systemFolderConfig: { include: false },
      setUp: {
        rootFolderName: "Individual Data Folders",
        rootFolderImgUrl: "gif/df_individual.gif",
      },
    });

    return individualRoots;
  }, []);

  const getTableFields = React.useCallback(
    (params: { folderId?: number; searchQuery?: string; context: number }) =>
      getFolderFields({
        folderId: params.folderId,
        // Only support Individual-level table fields in the new React builder.
        contextId: 1,
        searchQuery: params.searchQuery,
        includeTables: true,
        // Do not pull PDS fields when searching for table fields.
        includePDSFields: false,
      }).then((fields) => {
        // Use the parent table field (type 8) and de-duplicate by parent id.
        const tableFields = new Array<FieldPrimaryListDto & { fieldName: string; fieldId: number }>();
        fields.forEach((f) => {
          if (f?.type === FieldType.table && f?.id != null) {
            tableFields.push({
              ...f,
              fieldName: f.name,
              fieldId: f.id,
            });
          }
        });
        return tableFields;
      }),
    []
  );

  // Local state to control the table-field selector modal
  const [tableFieldSelectorOpened, setTableFieldSelectorOpened] = React.useState(false);

  const openTableFieldSelector = React.useCallback(() => {
    setTableFieldSelectorOpened(true);
  }, []);

  const closeTableFieldSelector = React.useCallback(() => {
    setTableFieldSelectorOpened(false);
  }, []);

  const handleTableFieldSelectorChange = React.useCallback(
    ({ field }: FieldSelectorOnChangeSingle) => {
      handleTableFieldChange({
        id: field.fieldId,
        name: field.fieldName,
        context: field.fieldContext,
      });
      closeTableFieldSelector();
    },
    [handleTableFieldChange, closeTableFieldSelector]
  );

  return (
    <Modal
      show
      backdrop="static"
      bsClass="bootstrap modal"
      className="DatasheetFormatManagement"
      dialogClassName="DatasheetFormatManagement__dialog"
      animation={false}
      onEscapeKeyDown={onClose}
      onHide={onClose}
    >
      <ModalHeader closeButton onHide={onClose}>
        <ModalTitle id={getId.title}>{modeConfig.title}</ModalTitle>
      </ModalHeader>
      <ModalBody>
        {!renameModalOpened && <MessageBox hideMessageBox={closeMessage} {...messageProps} />}
        <Flex justify="center">
          <div className="DatasheetFormatManagement__content">
            {mode !== DatasheetFormatManagementMode.import && (
            <Flex className="DatasheetFormatManagement__inner" alignItems="center">
              <span className="DatasheetFormatManagement__element-label">{trDts("Type")}</span>
              <div style={{ width: "100%" }}>
                <Dropdown
                  id={getId.formatTypes}
                  placeholder={trDts("Select")}
                  selectedValue={selectedFormatType}
                  searchable={false}
                  onValueChanged={setSelectedFormatType}
                  options={defaultFormatTypes}
                />
              </div>
            </Flex>)}
            {mode !== DatasheetFormatManagementMode.import && selectedFormatType === DatasheetFormatType.table && tableFieldSelectorOpened && (
              <FieldSelector
                withSearch
                getFolders={getTableFieldFolders}
                getFields={({
                  searchKeyword,
                  ...restParams
                }: FieldSelectorGetFieldParams) =>
                  getTableFields({
                    searchQuery: searchKeyword,
                    folderId: restParams.folderId,
                    context: restParams.context,
                  })
                }
                onChange={handleTableFieldSelectorChange}
                close={closeTableFieldSelector}
              />
            )}
            {mode !== DatasheetFormatManagementMode.import && selectedFormatType === DatasheetFormatType.table && (
              <Flex alignItems="center" width="100%" className="DatasheetFormatManagement__inner">
                <span className="DatasheetFormatManagement__element-label">Table</span>
                <Flex width="100%">
                  <Ellipsis
                    className="DatasheetFormatManagement__folder-chooser"
                    text={selectedTableFieldName || ""}
                  />
                  <Button
                    className="DatasheetFormatManagement__folder-chooser__open-btn"
                    variant="text"
                    onClick={openTableFieldSelector}
                  >
                    <img src="gif/df_warehouse.gif" />
                  </Button>
                </Flex>
              </Flex>
            )}
            {mode === DatasheetFormatManagementMode.export && (
              <Flex alignItems="center" width="100%" className="DatasheetFormatManagement__inner">
                <span className="DatasheetFormatManagement__element-label">{trDts("Format")}</span>
                <Flex width="100%">
                  <Ellipsis
                    className="DatasheetFormatManagement__folder-chooser"
                    text={selectedFormat?.name}
                  />
                  <Button
                    className="DatasheetFormatManagement__folder-chooser__open-btn"
                    variant="text"
                    id={getId.folderChooserBtn}
                    onClick={openFolderPicker}
                  >
                    <img src="gif/folder_open.gif" />
                  </Button>
                </Flex>
              </Flex>)}{mode !== DatasheetFormatManagementMode.export && mode !== DatasheetFormatManagementMode.import && (
              <Flex alignItems="center" width="100%" className="DatasheetFormatManagement__inner">
                <span className="DatasheetFormatManagement__element-label">{trDts("Folder")}</span>
                <Flex width="100%">
                  <Ellipsis
                    className="DatasheetFormatManagement__folder-chooser"
                    text={selectedFolder?.name}
                    id={getId.selectedFolderNameHolder}
                  />
                  <Button
                    className="DatasheetFormatManagement__folder-chooser__open-btn"
                    variant="text"
                    id={getId.folderChooserBtn}
                    onClick={openFolderPicker}
                  >
                    <img src="gif/folder_open.gif" />
                  </Button>
                </Flex>
              </Flex>
            )}
            {folderPickerOpened && (
              <FormatManagement
                selectedFolder={selectedFolder}
                selectedFormat={selectedFormat}
                title={modeConfig.formatManagement.title}
                submitBtnProps={modeConfig.formatManagement.submitBtnProps}
                close={closeFolderPicker}
                folders={folders}
                formats={formats}
                isAllowedToSubmit={
                  isAllowedToSubmitFormatManagementSubmit
                    ? isAllowedToSubmitFormatManagementSubmit
                    : modeConfig.formatManagement.isAllowedSubmit
                }
                getFormats={getAndSetFormats}
                resetFormats={resetFormats}
                getFolders={getAndSetFolders}
                onSubmit={handleFolderSelect}
                deleteFormatBtnProps={modeConfig.formatManagement.deleteFormatBtnProps}
                onFormatDelete={modeConfig.formatManagement.handleFormatDelete}
                onFolderDelete={modeConfig.formatManagement.handleFolderDelete}
                onFolderCreate={modeConfig.formatManagement.handleFolderCreate}
                onFormatRename={modeConfig.formatManagement.handleFormatRename}
              />
            )}
            {renameModalOpened && (
              <RenameFormatModal
                show={renameModalOpened}
                currentFormatName={renameFormatName}
                onClose={closeRenameModal}
                onConfirm={handleRenameConfirm}
                messageProps={messageProps}
                closeMessage={closeMessage}
              />
            )}
            {successToastMessage && (
              <div
                className="DatasheetFormatManagement__toast"
                role="status"
                aria-live="polite"
                onClick={clearSuccessToastMessage}
              >
                {successToastMessage}
              </div>
            )}
            {modeConfig.showFormatName && (
              <Flex alignItems="center" className="DatasheetFormatManagement__inner">
                <span className="DatasheetFormatManagement__element-label">{trDts("Name")}</span>
                <FormControl id={getId.formatName} value={formatName} onChange={handleFormatNameChange} />
              </Flex>
            )}
          </div>
        </Flex>
      </ModalBody>
      <ModalFooter>
        <Flex justify="flex-end">
          <Button id="btn-fm-cancel" onClick={onClose}>
            {trDts("Cancel")}
          </Button>
          <Button
            id="btn-fm-load"
            disabled={
              isExporting ||
              (mode !== DatasheetFormatManagementMode.load &&
              (isAllowedToSubmit
                ? !isAllowedToSubmit({
                    folderId: selectedFolder?.id,
                    formatId: selectedFormat?.id,
                    newFormatName: formatName,
                  })
                : !modeConfig.isAllowedToSubmit({
                    folderId: selectedFolder?.id,
                    formatId: selectedFormat?.id,
                    newFormatName: formatName,
                  })))
            }
            bsStyle="primary"
            onClick={handleSubmit}
          >
            {modeConfig.submitBtnText}
          </Button>
        </Flex>
      </ModalFooter>
    </Modal>
  );
};
